export enum Role {
    ADMINISTRATOR = 'Administrator',
    FLEET_MANAGER = 'FleetManager',
    FLEETUSER = 'Fleetuser',
    WORKSHOP_MANAGER = 'WorkshopManager',
    DISPATCHER = 'Dispatcher',
    HUMAN_RESOURCES = 'HumanResources',
    SHIPPER = 'Shipper',
    DRIVER = 'Driver',
    DRIVER_TRAINER = 'DriverTrainer',
    ANALYSIS_EXPERT = 'AnalysisExpert',
    BOOKING_EXPERT = 'BookingExpert',
    EE_DATA_ACT_USER = 'EuDataActUser',
    CROSSDOCK_USER = 'CrossdockUser',
}

export enum Service {
    PDC = 'roles.services.pdc',
    DIAGNOSTICS = 'roles.services.diagnostics',
    PERFORM = 'roles.services.perform',
    FLEET_MONITOR = 'roles.services.fleetMonitor',
    TIMED = 'roles.services.timed',
    SERVICE_CARE = 'roles.services.serviceCare',
    TACHO = 'roles.services.tacho',
    MARKETPLACE = 'roles.services.marketplace',
    EMANAGER = 'roles.services.eManager',
    MANNOW = 'roles.services.mannow',
    CHARGE_AND_GO = 'roles.services.chargeAndGo',

    USER_ADMIN = 'roles.services.userAdmin',
    DRIVER_ADMIN = 'roles.services.driverAdmin',
    VEHICLE_ADMIN = 'roles.services.vehicleAdmin',
    EU_DATA_ACT_REQUEST = 'roles.services.eudataactrequest',
    WEB_TMS = 'roles.services.WebTMS',
}

export enum Access {
    FULL = 'roles.description.fullAccess',
    RESTRICTED = 'roles.description.restrictedAccess',
    NO = 'roles.description.noAccess',
}

export interface RoleAccess {
    access: Access;
    details?: string;
}

const FULL: RoleAccess = { access: Access.FULL };
const RESTRICTED = (details: string): RoleAccess => {
    return { access: Access.RESTRICTED, details };
};
const NO: RoleAccess = { access: Access.NO };

export interface RoleDefinition {
    title: string;
    description: string;
    scope: string;
    services: Services;
    adminServices?: Services;
}
export type Services = Partial<Record<keyof typeof Service, RoleAccess>>;

export const RoleAccessMatrix: Record<Role, RoleDefinition> = {
    Administrator: {
        title: 'roles.names.rio.customer.base.FleetAdmin',
        description: 'roles.names.rio.customer.description.Administrator',
        scope: 'roles.description.accountBased',
        services: {
            PDC: FULL,
            DIAGNOSTICS: FULL,
            PERFORM: FULL,
            FLEET_MONITOR: FULL,
            TIMED: FULL,
            SERVICE_CARE: FULL,
            TACHO: FULL,
            MARKETPLACE: FULL,
            EMANAGER: FULL,
            MANNOW: FULL,
            CHARGE_AND_GO: FULL,
        },
        adminServices: {
            USER_ADMIN: FULL,
            DRIVER_ADMIN: FULL,
            VEHICLE_ADMIN: FULL,
            EU_DATA_ACT_REQUEST: FULL,
        },
    },
    FleetManager: {
        title: 'roles.names.FleetManager',
        description: 'roles.names.rio.customer.description.FleetManager',
        scope: 'roles.description.accountBased',
        services: {
            PDC: FULL,
            DIAGNOSTICS: NO,
            PERFORM: FULL,
            FLEET_MONITOR: FULL,
            TIMED: FULL,
            SERVICE_CARE: FULL,
            TACHO: FULL,
            MARKETPLACE: FULL,
            EMANAGER: FULL,
            MANNOW: FULL,
            CHARGE_AND_GO: FULL,
        },
        adminServices: {
            USER_ADMIN: NO,
            DRIVER_ADMIN: FULL,
            VEHICLE_ADMIN: FULL,
            EU_DATA_ACT_REQUEST: FULL,
        },
    },
    Fleetuser: {
        title: 'roles.names.rio.customer.base.FleetUser',
        description: 'roles.names.rio.customer.description.FleetUser',
        scope: 'roles.description.tagBased',
        services: {
            PDC: FULL,
            DIAGNOSTICS: NO,
            PERFORM: FULL,
            FLEET_MONITOR: FULL,
            TIMED: FULL,
            SERVICE_CARE: FULL,
            TACHO: FULL,
            MARKETPLACE: FULL,
            EMANAGER: RESTRICTED('roles.description.restriction.cannotControlCharging'),
            MANNOW: NO,
            CHARGE_AND_GO: RESTRICTED('roles.description.restriction.onlyChargingSessionAndCards'),
            // Can only see charging sessions and charging cards
        },
    },
    WorkshopManager: {
        title: 'roles.names.WorkshopManager',
        description: 'roles.names.rio.customer.description.WorkshopManager',
        scope: 'roles.description.tagBased',
        services: {
            PDC: FULL,
            DIAGNOSTICS: FULL,
            PERFORM: NO,
            FLEET_MONITOR: RESTRICTED('roles.description.restriction.viewMap'),
            TIMED: NO,
            SERVICE_CARE: FULL,
            TACHO: NO,
            MARKETPLACE: RESTRICTED('roles.description.restriction.cannotBookServices'),
            EMANAGER: RESTRICTED('roles.description.restriction.cannotControlCharging'),
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
    },
    Dispatcher: {
        title: 'roles.names.Dispatcher',
        description: 'roles.names.rio.customer.description.Dispatcher',
        scope: 'roles.description.tagBased',
        services: {
            PDC: NO,
            DIAGNOSTICS: NO,
            PERFORM: NO,
            FLEET_MONITOR: FULL,
            TIMED: FULL,
            SERVICE_CARE: FULL,
            TACHO: RESTRICTED('roles.description.restriction.dispatcher.tachograph'),
            MARKETPLACE: RESTRICTED('roles.description.restriction.cannotBookServices'),
            EMANAGER: FULL,
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
    },
    HumanResources: {
        title: 'roles.names.HumanResources',
        description: 'roles.names.rio.customer.description.HumanResources',
        scope: 'roles.description.tagBased',
        services: {
            PDC: NO,
            DIAGNOSTICS: NO,
            PERFORM: FULL,
            FLEET_MONITOR: FULL,
            TIMED: NO,
            SERVICE_CARE: NO,
            TACHO: RESTRICTED('roles.description.restriction.humanresources.tachograph'),
            MARKETPLACE: RESTRICTED('roles.description.restriction.cannotBookServices'),
            EMANAGER: NO,
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
    },
    Shipper: {
        title: 'roles.names.Shipper',
        description: 'roles.names.rio.customer.description.Shipper',
        scope: 'roles.description.tagBased',
        services: {
            PDC: NO,
            DIAGNOSTICS: NO,
            PERFORM: NO,
            FLEET_MONITOR: RESTRICTED('roles.description.restriction.viewMap'),
            TIMED: NO,
            SERVICE_CARE: NO,
            TACHO: NO,
            MARKETPLACE: NO,
            EMANAGER: NO,
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
    },
    Driver: {
        title: 'roles.names.rio.orderconnect.Driver',
        description: 'roles.names.rio.customer.description.Driver',
        scope: 'roles.description.tagBased',
        services: {
            PDC: NO,
            DIAGNOSTICS: NO,
            PERFORM: NO,
            FLEET_MONITOR: NO,
            TIMED: NO,
            SERVICE_CARE: NO,
            TACHO: NO,
            MARKETPLACE: RESTRICTED('roles.description.restriction.cannotBookServices'),
            EMANAGER: NO,
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
    },
    DriverTrainer: {
        title: 'roles.names.rio.perform.CoDriverTrainer',
        description: 'roles.names.rio.customer.description.CoDriverTrainer',
        scope: 'roles.description.accountBased',
        services: {
            PDC: NO,
            DIAGNOSTICS: NO,
            PERFORM: FULL,
            FLEET_MONITOR: NO,
            TIMED: NO,
            SERVICE_CARE: NO,
            TACHO: NO,
            MARKETPLACE: RESTRICTED('roles.description.restriction.cannotBookServices'),
            EMANAGER: NO,
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
    },
    BookingExpert: {
        title: 'roles.names.rio.mds.BookingExpert',
        description: 'roles.names.rio.mds.description.BookingExpert',
        scope: 'roles.description.accountBased',
        services: {
            PDC: NO,
            DIAGNOSTICS: NO,
            PERFORM: NO,
            FLEET_MONITOR: NO,
            TIMED: NO,
            SERVICE_CARE: NO,
            TACHO: NO,
            MARKETPLACE: FULL,
            EMANAGER: NO,
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
    },
    AnalysisExpert: {
        title: 'roles.names.rio.mds.AnalysisExpert',
        description: 'roles.names.rio.mds.description.AnalysisExpert',
        scope: 'roles.description.accountBased',
        services: {
            PDC: NO,
            DIAGNOSTICS: NO,
            PERFORM: FULL,
            FLEET_MONITOR: NO,
            TIMED: NO,
            SERVICE_CARE: NO,
            TACHO: NO,
            MARKETPLACE: NO,
            EMANAGER: NO,
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
    },
    EuDataActUser: {
        title: 'roles.names.rio.thirdparty.EuDataActUser',
        description: 'roles.names.rio.thirdparty.description.EuDataActUser',
        scope: 'roles.description.accountBased',
        services: {
            PDC: NO,
            DIAGNOSTICS: NO,
            PERFORM: NO,
            FLEET_MONITOR: NO,
            TIMED: NO,
            SERVICE_CARE: NO,
            TACHO: NO,
            MARKETPLACE: NO,
            EMANAGER: NO,
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
        adminServices: {
            EU_DATA_ACT_REQUEST: FULL,
        },
    },
    CrossdockUser: {
        title: 'roles.names.rio.crossdock.CrossdockUser',
        description: 'roles.names.rio.crossdock.description.CrossdockUser',
        scope: 'roles.description.accountBased',
        services: {
            PDC: NO,
            DIAGNOSTICS: NO,
            PERFORM: NO,
            FLEET_MONITOR: NO,
            TIMED: NO,
            SERVICE_CARE: NO,
            TACHO: NO,
            MARKETPLACE: NO,
            EMANAGER: NO,
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
        adminServices: {
            WEB_TMS: FULL,
        },
    },
};
